import React, { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useAudioRecorder } from "@/hooks/use-audio-recorder";
import { Button } from "./ui/button";
import { Mic, Square, MicIcon } from "lucide-react";

export type MicButtonProps = {
  renderInRecording?: (label:string) => React.ReactNode;
  renderNormal?: (label:string) => React.ReactNode;
  label?:string;
  onRecordStarted?: ()=>void;
  onRecordEnded?: ()=>void;
} & React.ComponentProps<"button">;

export const MicButton: React.FC<MicButtonProps> = ({ className, 
  label,
  renderInRecording,
  renderNormal,
  onRecordStarted,
  onRecordEnded,
  ...props }) => {
  const { isRecording, startRecording, stopRecording, isInitializing, speechDetected, vadInitialized, isReady } =
    useAudioRecorder();
  const [timerLabel, setTimerLabel] = useState("00:00");
  const hasStartedRecordingRef = useRef(false);
  const lastKeyPressTime = useRef(0); // For debouncing Enter key

  // Timer effect
  useEffect(() => {
    if (isRecording) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const seconds = Math.floor((Date.now() - startTime) / 1000);
        const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
        const secs = String(seconds % 60).padStart(2, "0");
        setTimerLabel(`${mins}:${secs}`);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setTimerLabel("00:00");
    }
  }, [isRecording]);
  
  // Handle recording start/end events
  useEffect(() => {
    if (isRecording && !hasStartedRecordingRef.current) {
      hasStartedRecordingRef.current = true;
      if (onRecordStarted) {
        onRecordStarted();
      }
    } else if (!isRecording && hasStartedRecordingRef.current) {
      hasStartedRecordingRef.current = false;
      if (onRecordEnded) {
        onRecordEnded();
      }
    }
  }, [isRecording, onRecordStarted, onRecordEnded]);


  const handlePointerDown = useCallback(() => {
    if (!isInitializing && isReady) {
      if (isRecording) {
        // If already recording, stop recording
        stopRecording();
      } else {
        // If not recording, start recording
        startRecording();
      }
    }
  }, [isRecording, isInitializing, isReady, startRecording, stopRecording]);

  // Prevent context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);
  
  // Global Enter key listener with debouncing
  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      // Only respond to Enter key
      if (e.key !== 'Enter') return;
      
      // Prevent default form submission or other Enter key behaviors
      e.preventDefault();
      
      // Debounce: ignore if pressed too quickly (within 300ms)
      const now = Date.now();
      if (now - lastKeyPressTime.current < 300) return;
      lastKeyPressTime.current = now;
      
      // Trigger the same action as clicking the button
      handlePointerDown();
    };
    
    // Add global listener
    window.addEventListener('keyup', handleKeyUp);
    
    // Cleanup
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handlePointerDown]);
  
  // Get visual states for better UX
  const getButtonState = () => {
    if (isInitializing || !isReady) return 'initializing';
    if (isRecording && speechDetected) return 'recording-with-speech';
    if (isRecording && !speechDetected) return 'recording-listening';
    return 'ready';
  };
  
  const buttonState = getButtonState();
  
  return (
    <Button
      onPointerDown={handlePointerDown}
      onContextMenu={handleContextMenu}
      disabled={isInitializing || !isReady}
      variant={isRecording ? "destructive" : "default"}
      className={cn(
        // Different animations based on state
        buttonState === 'recording-listening' && "animate-pulse",
        buttonState === 'recording-with-speech' && "scale-110",
        buttonState === 'initializing' && "opacity-50",
        "select-none touch-none transition-all duration-300 ease-out",
        "bg-red-600 text-xl",
        className
      )}
      style={{
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
      {...props}
    >
      {isRecording ? ( 
        renderInRecording ? renderInRecording(timerLabel) : 
        <>
          {speechDetected ? (
            <Square className="!size-9 fill-current text-white" />
          ) : (
            <MicIcon className="!size-9 text-white" />
          )}
          <span>{timerLabel}</span>
          {speechDetected && <span className="opacity-75">🎤 Listening...</span>}
        </>
      ) : ( 
        renderNormal ? renderNormal(label || "") :
        <>
          <MicIcon className="!size-9" />
          <span>{label}</span>
          {(!vadInitialized || !isReady) && <span className="opacity-50">Loading...</span>}
        </>
      )}
    </Button>
  );
};
